import React, { useEffect, useMemo, useState } from 'react'
import { Button, Drawer, Flex, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import useDownload from 'hooks/useDownload'
import { useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import * as Header from 'views/ui/Header/Header'
import { AntDHeader } from 'views/ui/Header/Header'
import { endpoints } from 'modules/endpoints'
import { Progress } from 'modules/types'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { SeedOrderStatus } from 'modules/domain/seedOrders/types'
import { getSeedInventoryGrowerCompanies } from 'modules/domain/company/managers'
import { getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import GenericSelect from 'views/components/Generics/GenericSelect'
import SeedOrdersList from './SeedOrdersList/SeedOrdersList'
import { seedTrackerVarietyLabel } from './SeedOrderAdd'
import Routes from './route'
import { FilterActionBar, ListHeaderBar, SearchStyle, TabContentWrapper, TableScrollWrapper } from './styles'

const pedigreeOptions = Object.values(SeedPedigree).map((v) => ({ label: v, value: v }))
const statusOptions = Object.values(SeedOrderStatus).map((v) => ({ label: v, value: v }))

const SeedOrdersTable: React.FC = () => {
  const { t } = useTranslation(['seedTracker', 'labels'])
  const push = useHistoryPush()
  const { Search } = Input

  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState<string | undefined>(undefined)
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)

  const [filterProductCardId, setFilterProductCardId] = useState<string | undefined>(undefined)
  const [filterCurrentPedigree, setFilterCurrentPedigree] = useState<string | undefined>(undefined)
  const [filterOriginCompanyId, setFilterOriginCompanyId] = useState<string | undefined>(undefined)
  const [filterDestinationCompanyId, setFilterDestinationCompanyId] = useState<string | undefined>(undefined)
  const [filterOrderStatus, setFilterOrderStatus] = useState<string | undefined>(undefined)

  const [growerOptions, setGrowerOptions] = useState<{ label: string; value: string }[]>([])
  const [varietyOptions, setVarietyOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    let cancelled = false
    getSeedInventoryGrowerCompanies()
      .then((res) => {
        if (!cancelled) {
          setGrowerOptions((res.data ?? []).map((c) => ({ value: c.id, label: c.official_name })))
        }
      })
      .catch(() => {
        if (!cancelled) setGrowerOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    getSeedTrackersList({}, {}, 1, 5000)
      .then((res) => {
        if (!cancelled) {
          setVarietyOptions((res.data ?? []).map((row) => ({ value: row.id, label: seedTrackerVarietyLabel(row) })))
        }
      })
      .catch(() => {
        if (!cancelled) setVarietyOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const [progressCSV, downloadCSV] = useDownload(endpoints.seedOrders('download/csv'), {
    ...(appliedSearch?.trim() ? { search: appliedSearch.trim() } : {}),
    ...(filterProductCardId ? { product_card_id: filterProductCardId } : {}),
    ...(filterCurrentPedigree ? { current_pedigree: filterCurrentPedigree } : {}),
    ...(filterOriginCompanyId ? { origin_company_id: filterOriginCompanyId } : {}),
    ...(filterDestinationCompanyId ? { destination_company_id: filterDestinationCompanyId } : {}),
    ...(filterOrderStatus ? { order_status: filterOrderStatus } : {}),
  })

  useHelmet({ title: t('list.tabs.seedOrders') })

  const drawerFilterSelectProps = useMemo(
    () => ({
      allowClear: true as const,
      showSearch: true as const,
      parentContainerFlex: 1 as const,
    }),
    [],
  )

  const growerFilterOption = (input: string, option: { label?: unknown } | undefined) => {
    const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '')
    return label.toLowerCase().includes(input.toLowerCase())
  }

  const varietyFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedOrderFilters.varietyLabel'),
    placeholder: t('list.seedOrderFilters.varietyPlaceholder'),
    options: varietyOptions,
    value: filterProductCardId,
    onChange: (value) => setFilterProductCardId((value as string | undefined) ?? undefined),
    filterOption: growerFilterOption,
    ...drawerFilterSelectProps,
  }

  const pedigreeFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedOrderFilters.pedigreeLabel'),
    placeholder: t('list.seedOrderFilters.pedigreePlaceholder'),
    options: pedigreeOptions,
    value: filterCurrentPedigree,
    onChange: (value) => setFilterCurrentPedigree((value as string | undefined) ?? undefined),
    ...drawerFilterSelectProps,
  }

  const sourceFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedOrderFilters.sourceLabel'),
    placeholder: t('list.seedOrderFilters.sourcePlaceholder'),
    options: growerOptions,
    value: filterOriginCompanyId,
    onChange: (value) => setFilterOriginCompanyId((value as string | undefined) ?? undefined),
    filterOption: growerFilterOption,
    ...drawerFilterSelectProps,
  }

  const destinationFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedOrderFilters.destinationLabel'),
    placeholder: t('list.seedOrderFilters.destinationPlaceholder'),
    options: growerOptions,
    value: filterDestinationCompanyId,
    onChange: (value) => setFilterDestinationCompanyId((value as string | undefined) ?? undefined),
    filterOption: growerFilterOption,
    ...drawerFilterSelectProps,
  }

  const statusFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedOrderFilters.statusLabel'),
    placeholder: t('list.seedOrderFilters.statusPlaceholder'),
    options: statusOptions,
    value: filterOrderStatus,
    onChange: (value) => setFilterOrderStatus((value as string | undefined) ?? undefined),
    ...drawerFilterSelectProps,
  }

  return (
    <>
      <ListHeaderBar justify={'space-between'}>
        <Header.Title title={t('list.tabs.seedOrders')} />
        <AntDHeader />
      </ListHeaderBar>
      <TabContentWrapper>
        <FilterActionBar vertical gap="middle">
          <Flex justify="space-between" align="center" flex={1} gap="middle" wrap="wrap">
            <Flex gap="middle" flex={1} align="center" wrap="wrap">
              <Search
                placeholder={t('list.searchPlaceholder')}
                onSearch={(value) => setAppliedSearch(value?.trim() || undefined)}
                value={searchInput}
                allowClear
                onChange={(e) => setSearchInput(e.target.value)}
                style={SearchStyle}
              />
              <Button type="default" onClick={() => setFiltersDrawerOpen(true)} style={{ display: 'none' }}>
                {t('detail.seedOrders.filtersButton')}
              </Button>
            </Flex>
            <Flex gap="middle" wrap="wrap">
              <Button type="default" onClick={downloadCSV} loading={progressCSV === Progress.WORK}>
                {t('labels:downloadCsv')}
              </Button>
              <Button type="primary" onClick={() => push({ route: Routes.SeedOrderAdd })}>
                {t('list.createOrder')}
              </Button>
            </Flex>
          </Flex>
          <Flex gap="middle" flex={1} align="center" wrap="wrap">
            <GenericSelect {...varietyFilterProps} />
            <GenericSelect {...pedigreeFilterProps} />
            <GenericSelect {...sourceFilterProps} />
            <GenericSelect {...destinationFilterProps} />
            <GenericSelect {...statusFilterProps} />
          </Flex>
        </FilterActionBar>
        <TableScrollWrapper>
          <SeedOrdersList
            search={appliedSearch}
            productCardId={filterProductCardId}
            currentPedigree={filterCurrentPedigree}
            originCompanyId={filterOriginCompanyId}
            destinationCompanyId={filterDestinationCompanyId}
            orderStatus={filterOrderStatus}
          />
        </TableScrollWrapper>
        <Drawer
          title={t('detail.seedOrders.filtersDrawerTitle')}
          placement="right"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          width={378}
          destroyOnClose={false}
        >
          <Flex vertical gap="middle">
            <GenericSelect {...varietyFilterProps} />
            <GenericSelect {...pedigreeFilterProps} />
            <GenericSelect {...sourceFilterProps} />
            <GenericSelect {...destinationFilterProps} />
            <GenericSelect {...statusFilterProps} />
          </Flex>
        </Drawer>
      </TabContentWrapper>
    </>
  )
}

export default SeedOrdersTable
